/**
 * Client portal API (/portal/*). Every call is scoped server-side to the
 * signed-in client's own link — the browser never sends a client id.
 */

import type {
  PortalApprovalSummary,
  PortalApprovalView,
  PortalDecisionInput,
  PortalDocument,
  PortalInvoice,
  PortalMe,
  PortalMoodboard,
  PortalProjectSummary,
  PortalPurchase,
  PortalReceipt,
} from '@nord-living/shared';

import api from '@/services/api';

type Wrapped<T> = { data: T };
const unwrap = <T>(res: { data: Wrapped<T> }) => res.data.data;
/** Optional `?projectId=` filter (omitted = every project of the client). */
const byProject = (projectId?: string) => ({ params: projectId ? { projectId } : {} });

export const getPortalMe = () => api.get<Wrapped<PortalMe>>('/portal/me').then(unwrap);

export const getPortalProject = (id: string) => api.get<Wrapped<PortalProjectSummary>>(`/portal/projects/${encodeURIComponent(id)}`).then(unwrap);

export const listPortalInvoices = (projectId?: string) =>
  api.get<Wrapped<PortalInvoice[]>>('/portal/invoices', byProject(projectId)).then(unwrap);

export const listPortalReceipts = () => api.get<Wrapped<PortalReceipt[]>>('/portal/receipts').then(unwrap);

export const listPortalPurchases = (projectId?: string) =>
  api.get<Wrapped<PortalPurchase[]>>('/portal/purchases', byProject(projectId)).then(unwrap);

export const listPortalApprovals = (projectId?: string) =>
  api.get<Wrapped<PortalApprovalSummary[]>>('/portal/approvals', byProject(projectId)).then(unwrap);

export const getPortalApproval = (id: string) => api.get<Wrapped<PortalApprovalView>>(`/portal/approvals/${encodeURIComponent(id)}`).then(unwrap);

export const decidePortalItem = (requestId: string, itemId: string, input: PortalDecisionInput) =>
  api.post<Wrapped<PortalApprovalView>>(`/portal/approvals/${encodeURIComponent(requestId)}/items/${encodeURIComponent(itemId)}/decision`, input).then(unwrap);

export const listPortalDocuments = (projectId?: string) =>
  api.get<Wrapped<PortalDocument[]>>('/portal/documents', byProject(projectId)).then(unwrap);

export const listPortalMoodboards = (projectId?: string) =>
  api.get<Wrapped<PortalMoodboard[]>>('/portal/moodboards', byProject(projectId)).then(unwrap);
